/**
 * Locale registry for the UI chrome (buttons, labels, fallbacks). CMS content
 * is localized in Payload; these are the strings the code itself renders.
 *
 * `Messages` is derived from da.ts, so da stays the single source of truth for
 * the key shape; en.ts is typed `Messages` and must mirror it exactly.
 *
 * Catalogs exist for `da` and `en`. If the site's locales are something else,
 * add a catalog file (copy en.ts) and register it in CATALOGS — until then the
 * default locale falls back to the Danish and the secondary to the English one.
 */
import { DEFAULT_LOCALE, SECONDARY_LOCALE } from '@/site.config'
import { da } from './da'
import { en } from './en'

export { DEFAULT_LOCALE, SECONDARY_LOCALE }

export const LOCALES = [DEFAULT_LOCALE, SECONDARY_LOCALE] as const
export type Locale = (typeof LOCALES)[number]

/**
 * Widen literal types out of `typeof da` (which is `as const`). Keeps the key
 * SHAPE (and function signatures) but loosens leaf literals to their base
 * types, so en.ts must mirror the keys exactly while supplying its own text.
 */
type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends (...args: infer A) => infer R
        ? (...args: A) => R
        : T extends readonly (infer E)[]
          ? readonly Widen<E>[]
          : T extends object
            ? { [K in keyof T]: Widen<T[K]> }
            : T

export type Messages = Widen<typeof da>

const CATALOGS: Record<string, Messages> = { da, en }

export const messages: Record<Locale, Messages> = {
  [DEFAULT_LOCALE]: CATALOGS[DEFAULT_LOCALE] ?? da,
  [SECONDARY_LOCALE]: CATALOGS[SECONDARY_LOCALE] ?? en,
} as Record<Locale, Messages>

/** Narrow an arbitrary string (e.g. a `:lang` route param) to a Locale. */
export function isLocale(value: string | undefined): value is Locale {
  return value !== undefined && (LOCALES as readonly string[]).includes(value)
}
